import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ChevronUp, CreditCard, ExternalLink, Keyboard, LogOut, UserRound } from "lucide-react";
import { sb, signOutEverywhere, usesAccounts } from "../lib/supabase";
import { CONFIG } from "../lib/config";
import { lastCompany } from "../companies/useMe";

// The person's menu, at the foot of the side menu: who is signed in and in what role, their profile, password and
// two-step sign-in, changing company, the keyboard codes, and signing out.
export function UserMenu({ email, role, via, onSwitch, onHelp }: { email?: string; role?: string; via?: string | null; onSwitch: () => void; onHelp: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => { void sb.auth.getUser().then(({ data }) => setName(String(data.user?.user_metadata?.full_name ?? ""))); }, []);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); } };
    document.addEventListener("mousedown", away); document.addEventListener("keydown", esc, true);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc, true); };
  }, [open]);
  const who = name || email || "";
  const initials = who.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";
  const close = () => setOpen(false);
  return (
    <div className="user-menu" ref={box}>
      {open && <div className="user-pop" role="menu">
        <div className="user-pop-head"><b>{name || "Signed in as"}</b><span>{email}</span></div>
        {/* on Altekore's domains these are managed in Altekore Accounts, for every product at once */}
        {/* profile, password, two-step sign-in and devices: all in the Altekore account, opened in its own tab */}
        <Item to="" icon={<UserRound size={16} aria-hidden="true" />} label="My Account" onClick={close} />
        <Link role="menuitem" to="/account#billing" onClick={close}><CreditCard size={16} aria-hidden="true" />Plan and Billing</Link>
        <button role="menuitem" type="button" onClick={() => { close(); onSwitch(); }}><Building2 size={16} aria-hidden="true" />Change Company</button>
        <button role="menuitem" type="button" onClick={() => { close(); onHelp(); }}><Keyboard size={16} aria-hidden="true" />Keyboard and Codes</button>
        <button role="menuitem" type="button" className="sign-out" onClick={() => { lastCompany.clear(); void signOutEverywhere(); }}><LogOut size={16} aria-hidden="true" />Sign Out</button>
      </div>}
      <button type="button" className="user-button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)} title="Your profile and sign out">
        <span className="avatar" aria-hidden="true">{initials}</span>
        <span className="user-who"><span className="user-name">{name || email}</span><span className="user-role">{role}{via ? ` via ${via}` : ""}</span></span>
        <ChevronUp size={14} aria-hidden="true" className={open ? "" : "flip"} />
      </button>
    </div>
  );
}

function Item({ to, icon, label, onClick }: { to: string; icon: React.ReactNode; label: string; onClick: () => void }) {
  return usesAccounts()
    ? <a role="menuitem" href={`${CONFIG.accountsUrl}/${to}`} target="_blank" rel="noopener" onClick={onClick}>{icon}{label}<ExternalLink size={13} className="ext" aria-label="(opens in a new tab)" /></a>
    : <Link role="menuitem" to={`/account#${to || "profile"}`} onClick={onClick}>{icon}{label}</Link>;
}
